'use client'

import { useCallback, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'

export interface SearchItem {
  id: string
  title: string
  subtitle?: string
  url: string
}

export interface SearchResultGroup {
  type: string
  items: SearchItem[]
}

export interface UseCommandSearchStateOptions {
  onOpenChat?: (intent: string) => void
  onSelectThread?: (threadId: string) => void
  isLoggedIn?: boolean
}

export function useCommandSearchState({
  onOpenChat,
  onSelectThread,
  isLoggedIn = true,
}: UseCommandSearchStateOptions) {
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResultGroup[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchResults = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([])
      setLoading(false)
      return
    }
    if (!isLoggedIn) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`)
      const data = await res.json()
      setResults(data.results ?? [])
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [isLoggedIn])

  const handleValueChange = useCallback(
    (value: string) => {
      setQuery(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => fetchResults(value), 300)
    },
    [fetchResults]
  )

  const handleSelect = useCallback(
    (url: string) => {
      router.push(url)
    },
    [router]
  )

  const handleSelectThread = useCallback(
    (threadId: string) => {
      onSelectThread?.(threadId)
    },
    [onSelectThread]
  )

  const handleNewIntent = useCallback(
    (intent: string) => {
      onOpenChat?.(intent)
    },
    [onOpenChat]
  )

  const reset = useCallback(() => {
    setQuery('')
    setResults([])
  }, [])

  const hasResults = results.length > 0
  const showNav = query.trim() === ''

  return {
    query,
    setQuery,
    results,
    loading,
    handleValueChange,
    handleSelect,
    handleSelectThread,
    handleNewIntent,
    hasResults,
    showNav,
    reset,
  }
}
